import { Trans } from 'react-i18next';
import { SortKey, TableHeader } from './table.type';
import AscSort from '../../assets/asc-sort.png';
import DescSort from '../../assets/sort.png';
import KebabIcon from '../../assets/kebab.png';
import './table.css';
import Tooltip from '../tooltip/tooltip';
import { TooltipLength } from './table.type';
import Checkbox from '../checkbox/checkbox';
import Loading from '../../assets/loading/loading';
import DropdownMenu from '../dropDownMenu/dropDownMenu';
import { OptionWithLoading } from '../../pages/userManagement/userManagement.type';
import { getIdAttributes } from '../../utils/helper';
import toString from 'lodash/toString';

export const idPrefix: string = 'table';

/**
 * Renders the sortable indicator for table headers based on the current sort state.
 * Displays an ascending arrow (▲) for ascending sort, a descending arrow (▼) for descending sort,
 * or a default sorting icon when no sorting is applied.
 *
 * @param {string} key - The key of the column that is being evaluated for sorting.
 * @param {string | null} sortKey - The key of the column currently being sorted.
 * @param {SortKey.ASC | SortKey.DESC} sortOrder - The current sort order for the table, either ascending (ASC) or descending (DESC).
 *
 * @returns {JSX.Element} - The JSX element that represents the sort indicator (▲, ▼, or default icon).
 */
export const renderSortableIndicator = <T extends object>(
  key: keyof T | undefined,
  sortKey: keyof T | null,
  sortOrder: SortKey,
): JSX.Element => {
  if (sortKey === key) {
    return sortOrder === SortKey.ASC ? (
      <img
        src={AscSort}
        alt="Descending sort"
        className="w-[12px] h-[6px]"
        {...getIdAttributes(idPrefix, 'sort-asc')}
      />
    ) : (
      <img
        src={DescSort}
        alt="Descending sort"
        className="w-[12px] h-[6px]"
        {...getIdAttributes(idPrefix, 'sort-desc')}
      />
    );
  }
  return (
    <img
      src={DescSort}
      alt="Descending sort"
      className="w-[12px] h-[6px]"
      {...getIdAttributes(idPrefix, 'sort-default')}
    />
  );
};

/**
 * Generates a JSX <tr> element representing the header row of a table.
 *
 * This function maps over the provided headers and creates a table header cell (`<td>`) for each column.
 * If a column is sortable, it adds sorting icons and toggles the sort order on click.
 * An optional "Action" column is included at the end of the row.
 *
 * @param headers - Array of objects defining each table column (key, label, and sortable flag).
 * @param sortKey - The currently sorted column key, or null if none.
 * @param sortOrder - The current sort direction: 'asc' or 'desc'.
 * @param handleSort - Callback function triggered when a sortable header is clicked.
 * @param includeActionColumn - Optional boolean to include an "Action" column at the end (default: true).
 * @returns A JSX element representing the table header row.
 */
export const renderTableHeader = <T extends object>(
  headers: TableHeader<T>[],
  allSelected: boolean,
  onToggleSelectAll: () => void,
  sortKey: keyof T | null,
  sortOrder: SortKey,
  handleSort: (key: keyof T) => void,
  isTablet: boolean,
): JSX.Element => {
  return (
    <tr {...getIdAttributes(idPrefix, 'header-row')}>
      {!isTablet && (
        <td {...getIdAttributes(idPrefix, 'header-checkbox')}>
          <Checkbox
            checked={allSelected}
            onChange={onToggleSelectAll}
            className=""
          />
        </td>
      )}
      {headers.map((col: TableHeader<T>, index: number) => (
        <th
          key={toString(col.key) || index}
          className={`${isTablet ? 'header-text-tablet' : 'header-text'} pb-4 ${
            col.sortable ? 'cursor-pointer select-none' : ''
          }`}
          {...getIdAttributes(
            idPrefix,
            `header-col-${toString(col.key) || index}`,
          )}
        >
          <span className="flex items-center gap-1">
            {col.header}
            {col.sortable && (
              <span
                onClick={() => col.key && handleSort(col.key)}
                className="ml-2"
                {...getIdAttributes(
                  idPrefix,
                  `header-sort-${toString(col.key) || index}`,
                )}
              >
                {renderSortableIndicator(col.key, sortKey, sortOrder)}
              </span>
            )}
          </span>
        </th>
      ))}
      <th
        className="action-text pb-4 pr-10 text-right"
        {...getIdAttributes(idPrefix, 'header-col-action')}
      >
        <Trans i18nKey="userManagement.table.action" />
      </th>
    </tr>
  );
};

/**
 * Renders the content of a table cell, applying truncation and wrapping it in a flex container.
 * This function accepts a row and a header, using the `generateCellContent` method from the header
 * to generate the content to display for that specific cell.
 *
 * @param row - The current row of data, which will be used to generate the cell content.
 * @param header - The header for the column, which contains the `generateCellContent` method
 *                 that accepts a row and returns the content for that specific cell.
 *
 * @returns A JSX element that represents the rendered cell content, with truncation applied
 *          to ensure it does not overflow the given width.
 */
const renderCellContent = <T,>(
  row: T,
  header: { generateCellContent: (row: T) => React.ReactNode },
) => {
  return (
    <div className="flex items-center gap-2">
      <div>{header.generateCellContent(row)}</div>
    </div>
  );
};

/**
 * Renders a single table row for the given data entry.
 *
 * @template T - The type of each data row.
 * @param {T} row - The data object representing a single row.
 * @param {number} rowIndex - The index of the row in the dataset.
 * @param {TableHeader<T>[]} tableHeader - An array describing the table header, with keys and labels.
 * @param {number | null} selectedRow - The currently selected row index, used to apply a highlight.
 * @param {(index: number) => void} handleRowClick - Callback function to handle row click event.
 *
 * @returns {JSX.Element} A JSX element representing the rendered table row.
 *
 * @description
 * This function renders a table row (`<tr>`) with cells (`<td>`) for each header key. It highlights the selected row,
 * adds a tooltip and status indicator dot if the row contains a `STATUS` field, and appends a kebab (options) icon at the end.
 * It is intended to be used inside a `.map()` to generate multiple rows in a table body.
 */
export const renderTableRow = <T, KebabAction extends string = string>(
  row: T,
  rowIndex: number,
  tableHeader: TableHeader<T>[],
  selectedRowIndex: number | null,
  handleRowClick: (row: T, rowIndex: number) => void,
  checked: boolean,
  onChange: () => void,
  openMenuRowIndex: number | null,
  handleKebabClick: (index: number) => void,
  onKebabSelect: (action: KebabAction, row: T, rowIndex: number) => void,
  loadingMenu:
    | { rowIndex: number; action: KebabAction }
    | { rowIndex: -1; action: '' },
  getKebabMenuOptions: <T>(
    row: T,
    loadingMenu:
      | { rowIndex: number; action: KebabAction }
      | { rowIndex: -1; action: '' },
    rowIndex: number,
  ) => OptionWithLoading<KebabAction>[],
  isTablet: boolean,
) => {
  return (
    <tr
      key={rowIndex}
      onClick={() => handleRowClick(row, rowIndex)}
      className={`cursor-pointer ${rowIndex !== 0 ? 'border-t' : ''} ${
        selectedRowIndex === rowIndex ? 'bg-TABLE_HOVER_COLOR' : ''
      } hover:bg-TABLE_HOVER_COLOR`}
      {...getIdAttributes(idPrefix, `row-${rowIndex}`)}
    >
      {!isTablet && (
        <td className="align-middle" {...getIdAttributes(idPrefix, `row-checkbox-${rowIndex}`)}>
          <Checkbox
            checked={checked}
            onChange={onChange}
            onClick={(e: React.MouseEvent<HTMLInputElement>) =>
              e.stopPropagation()
            }
          />
        </td>
      )}
      {tableHeader.map((header, index) => {
        const content = header.generateCellContent(row);
        return (
          <td
            key={index}
            className={`px-6 py-4 ${isTablet ? 'text-base' : 'text-sm'} ${
              header.getCellClassName
                ? header.getCellClassName(row)
                : 'text-[#030303]'
            }`}
            {...getIdAttributes(idPrefix, `row-${rowIndex}-col-${index}`)}
          >
            {String(content).length < TooltipLength ? (
              <div>{renderCellContent(row, header)}</div>
            ) : (
              <Tooltip tooltipContent={String(content)}>
                <div>{renderCellContent(row, header)}</div>
              </Tooltip>
            )}
          </td>
        );
      })}

      <td
        className="px-2 py-3 min-w-[20px]"
        {...getIdAttributes(idPrefix, `row-${rowIndex}-action`)}
      >
        <div className="flex ml-2 min-h-[24px]">
          <img
            src={KebabIcon}
            alt="Kebab icon"
            className="w-5 h-5 cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              handleKebabClick(rowIndex);
            }}
            {...getIdAttributes(idPrefix, `row-${rowIndex}-kebab`)}
          />
        </div>

        {openMenuRowIndex === rowIndex && (
          <div
            className="relative top-full right-20 z-50"
            {...getIdAttributes(idPrefix, `row-${rowIndex}-dropdown`)}
          >
            <DropdownMenu
              className="whitespace-nowrap"
              options={getKebabMenuOptions(row, loadingMenu, rowIndex)}
              onSelect={(action: KebabAction) =>
                onKebabSelect(action, row, rowIndex)
              }
              onClose={() => handleKebabClick(rowIndex)}
            />
          </div>
        )}
      </td>
    </tr>
  );
};

/**
 * Renders a centered "No data to display" row within a table when there is no data.
 *
 * @template T - Generic type for consistency with other table rendering functions.
 * @param {number} columnCount - The total number of columns in the table (including actions column),
 *                                used to correctly span the message across the table width.
 * @returns {JSX.Element} A <tbody> element containing a single centered row with a no-data message.
 */
export const renderNoDataRow = <T,>(columnCount: number): JSX.Element => {
  return (
    <tbody {...getIdAttributes(idPrefix, 'no-data-tbody')}>
      <tr {...getIdAttributes(idPrefix, 'no-data-row')}>
        <td
          {...getIdAttributes(idPrefix, 'no-data-td')}
          colSpan={columnCount}
          className="h-[60dvh]"
        >
          <div
            className="flex justify-center items-center h-full text-gray-500 text-center"
            {...getIdAttributes(idPrefix, 'no-data-text')}
          >
            <Trans i18nKey="userManagement.table.noData" />
          </div>
        </td>
      </tr>
    </tbody>
  );
};

/**
 * Renders a loading row for a table, typically used when data is being fetched.
 * The row spans across all table columns and centers the loader vertically and horizontally.
 *
 * @param colSpan - Number of columns in the table, used to span the loading row across all columns.
 * @returns A <tbody> element containing a single row with a centered loading indicator.
 */
export const renderLoadingRow = (colSpan: number) => (
  <tbody {...getIdAttributes(idPrefix, 'loading-tbody')}>
    <tr {...getIdAttributes(idPrefix, 'loading-row')}>
      <td colSpan={colSpan} {...getIdAttributes(idPrefix, 'loading-td')}>
        <div
          className="flex items-center justify-center h-[60dvh]"
          {...getIdAttributes(idPrefix, 'loading-text')}
        >
          <div role="status">
            <Loading />
          </div>
        </div>
      </td>
    </tr>
  </tbody>
);
